// Read-only detail view for a milestone. Renders through the shared
// TrackingDetailPanel: the V2 status badge composes into the panel's
// badges slot, the milestone fields (SOW link, planned date, the
// four-timestamp lifecycle ladder, payment amount) into its <dl>, and the
// linked-deliverables section, awaiting-acceptance callout, description,
// acceptance criteria, evidence links + notes into its children slot.
//
// Surfaces the deliverables linked to the milestone via the FK on each
// deliverable (deliverable.milestoneId === item.id) using the shared
// LinkedDeliverablesSection. Rows are clickable via onNavigateToDeliverable.

import { Pencil, ExternalLink, AlertTriangle } from "lucide-react";
import { safeExternalHref } from "../../../../utils/evidenceLinks";
import Button from "../../../common/Button";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import OpenIssueCountBadge from "../shared/OpenIssueCountBadge";
import RaiseIssueButton from "../shared/RaiseIssueButton";
import { deriveOverdue } from "../../../../utils/deriveMilestoneStatus";
import TrackingDetailPanel, {
  type TrackingDetailField,
} from "../shared/TrackingDetailPanel";
import LinkedDeliverablesSection from "./LinkedDeliverablesSection";
import { useLinkedDeliverables } from "../../../../hooks/useTracking";
import { formatSowDisplayNumber } from "./formatSowDisplayNumber";
import { deriveAwaitingFullAcceptance } from "../../../../utils/deriveMilestoneStatus";
import { deriveMilestoneDisplayStatus } from "../../../../utils/deriveMilestoneDisplayStatus";
import { deriveMilestoneDisplayLifecycle } from "../../../../utils/deriveMilestoneDisplayLifecycle";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { formatAud2dp } from "../../../../utils/formatCurrency";
import type { Milestone, Sow } from "../../../../types";

interface MilestoneDetailExpandProps {
  item: Milestone;
  /** SOWs loaded contract-wide upstream so the parent SOW row can resolve
   *  `item.sowId` to its number/name without spawning a per-row fetch.
   *  When omitted the SOW row falls back to the raw id label. */
  sows?: readonly Sow[];
  onEdit?: () => void;
  /** Callback fired when a linked-deliverable row is clicked. Parent
   *  wires this to a route change that navigates to the deliverables
   *  tab. When omitted, rows render but click is a no-op. */
  onNavigateToDeliverable?: (deliverableId: string) => void;
  /** Callback fired when the SOW link is clicked. Parent wires this to
   *  a route change that navigates to the variations-sows tab and
   *  expands the referenced SOW row. */
  onNavigateToSow?: (sowId: string) => void;
}

export default function MilestoneDetailExpand({
  item,
  sows,
  onEdit,
  onNavigateToDeliverable,
  onNavigateToSow,
}: MilestoneDetailExpandProps) {
  const parentSow = item.sowId
    ? sows?.find((s) => s.id === item.sowId)
    : undefined;
  const linkedQuery = useLinkedDeliverables(item.contractId, item.id);

  const awaitingFullAcceptance = deriveAwaitingFullAcceptance(item);

  const fields: TrackingDetailField[] = [];
  if (item.sowId) {
    fields.push({
      label: "Statement of work",
      value: parentSow ? (
        onNavigateToSow ? (
          <button
            type="button"
            onClick={() => onNavigateToSow(parentSow.id)}
            data-testid="milestone-sow-link"
            aria-label={`Navigate to ${formatSowDisplayNumber(parentSow.number)} ${parentSow.name}`}
            className="inline-flex items-center gap-1 rounded-sm font-medium text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
          >
            <span className="font-mono">
              {formatSowDisplayNumber(parentSow.number)}
            </span>
            <span>· {parentSow.name}</span>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : (
          <span data-testid="milestone-sow-static">
            <span className="font-mono">
              {formatSowDisplayNumber(parentSow.number)}
            </span>
            <span> · {parentSow.name}</span>
          </span>
        )
      ) : (
        <span
          data-testid="milestone-sow-unresolved"
          className="font-mono text-slate-500 dark:text-slate-400"
          title="Referenced SOW could not be resolved"
        >
          {item.sowId}
        </span>
      ),
    });
  }
  fields.push({
    label: "Planned date",
    value: formatDayMonthYear(item.plannedDate),
  });
  if (item.deliveredAt) {
    fields.push({
      label: "Delivered",
      value: formatDayMonthYear(item.deliveredAt),
      comment: item.deliveredComment,
      commentTestId: "milestone-delivered-comment",
    });
  }
  if (item.partiallyAcceptedAt) {
    fields.push({
      label: "Partially accepted",
      value: formatDayMonthYear(item.partiallyAcceptedAt),
      comment: item.partiallyAcceptedComment,
      commentTestId: "milestone-partially-accepted-comment",
    });
  }
  if (item.acceptedAt) {
    fields.push({
      label: "Accepted",
      value: formatDayMonthYear(item.acceptedAt),
      comment: item.acceptedComment,
      commentTestId: "milestone-accepted-comment",
    });
  }
  if (item.paidAt) {
    fields.push({
      label: "Paid",
      value: formatDayMonthYear(item.paidAt),
      comment: item.paidComment,
      commentTestId: "milestone-paid-comment",
    });
  }
  fields.push({
    label: "Payment amount",
    value:
      typeof item.paymentAmount === "number"
        ? formatAud2dp(item.paymentAmount)
        : "—",
  });

  return (
    <TrackingDetailPanel
      testId="milestone-detail-expand"
      title={`#${item.milestoneNumber}${item.milestoneSuffix ?? ""} — ${item.name}`}
      badges={
        <>
        <TrackingStatusBadge
          kind="milestone"
          status={(() => {
            // Pass the same V2 shape the table-row status column uses so
            // the detail header carries overlay info (e.g. Delivered late)
            // and the partial+paid pair, instead of collapsing to a bare
            // lifecycle pill that can read as "Delivered" for a milestone
            // that was actually delivered late.
            const display = deriveMilestoneDisplayStatus(item);
            const lifecycle = deriveMilestoneDisplayLifecycle(item);
            const isPartialPaid =
              lifecycle === "partially-accepted" && Boolean(item.paidAt);
            return {
              ...display,
              lifecycle,
              ...(isPartialPaid ? { secondaryPill: "paid" as const } : {}),
            };
          })()}
        />
        <OpenIssueCountBadge
          contractId={item.contractId}
          targetType="milestone"
          targetId={item.id}
        />
        </>
      }
      fields={fields}
      actions={
        <>
          {/* Failing-signal raise: an overdue (not yet delivered, planned
           *  date in the past) milestone spawns a tracked issue auto-linked
           *  to this row. The check uses the same `deriveOverdue` the
           *  timeline + register reads, so the affordance shows iff the
           *  signal genuinely fires elsewhere on the surface. */}
          {deriveOverdue(item) && (
            <RaiseIssueButton
              contractId={item.contractId}
              srcType="milestone"
              srcId={item.id}
              srcTitle={item.name}
            />
          )}
          {onEdit ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          ) : null}
        </>
      }
    >
      <LinkedDeliverablesSection
        query={linkedQuery}
        onNavigate={onNavigateToDeliverable}
      />

      {awaitingFullAcceptance && (
        <div
          role="status"
          data-testid="awaiting-acceptance-callout"
          className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-caption text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <AlertTriangle
            className="h-4 w-4 flex-none text-amber-600 dark:text-amber-400"
            aria-hidden="true"
          />
          <span>
            Remainder of milestone still to be accepted. Tick &ldquo;Mark as
            accepted&rdquo; once the supplier has re-delivered and you&rsquo;ve
            signed off the rest.
          </span>
        </div>
      )}

      {item.description && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Description
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.description}
          </p>
        </div>
      )}

      {item.acceptanceCriteria && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Acceptance criteria / evidence required
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.acceptanceCriteria}
          </p>
        </div>
      )}

      {item.evidenceLinks && item.evidenceLinks.length > 0 && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Evidence links
          </h4>
          <ul className="mt-2 space-y-1">
            {item.evidenceLinks.map((link, i) => {
              const href = safeExternalHref(link.url);
              const text = link.label || link.url;
              return (
                <li key={i}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-body text-brand-600 hover:text-brand-500 dark:text-brand-400 hover:underline"
                    >
                      {text}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-body text-slate-700 dark:text-slate-300">
                      {text}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {item.notes && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Notes
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.notes}
          </p>
        </div>
      )}
    </TrackingDetailPanel>
  );
}
